import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiAuth } from "../../lib/api";
import {
  patientMessagingApi,
  DeleteMessageResult,
  DirectResult,
  LeaveResult,
  MarkReadResult,
  MemberRef,
  PatientConversationView,
  ThreadPage,
  UnreadSummary,
} from "./api";
import { withoutConversation } from "./unread";

const UNREAD_KEY = "patient-messaging-unread";
const CONVERSATIONS_KEY = "patient-messaging-conversations";
const THREAD_KEY = "patient-messaging-thread";

export function patientUnreadKey(patientId: string | null | undefined) {
  return [UNREAD_KEY, patientId ?? null] as const;
}

export function patientConversationsKey(patientId: string | null | undefined) {
  return [CONVERSATIONS_KEY, patientId ?? null] as const;
}

export function patientThreadKey(patientId: string | null | undefined, conversationId: string | null | undefined) {
  return [THREAD_KEY, patientId ?? null, conversationId ?? null] as const;
}

type UnreadOptions = { patientId: string | null | undefined; enabled: boolean };

export function usePatientUnreadSummary(auth: ApiAuth, { patientId, enabled }: UnreadOptions) {
  const active = enabled && !!patientId && !!auth.jwt;
  return useQuery({
    queryKey: patientUnreadKey(patientId),
    queryFn: () => patientMessagingApi.unreadSummary(auth),
    enabled: active,
    staleTime: 15_000,
    refetchInterval: active ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useInvalidatePatientUnread(patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: patientUnreadKey(patientId) });
}

/** What a read says: the conversation, and the stamp of the latest message the screen showed (ADR 0018 §5). */
export type ReadShown = { conversationId: string; upTo: number | null };

export function useMarkConversationRead(auth: ApiAuth, patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  const queryKey = patientUnreadKey(patientId);
  return useMutation<MarkReadResult, unknown, ReadShown, { previous?: UnreadSummary }>({
    mutationFn: ({ conversationId, upTo }) => patientMessagingApi.markRead(auth, conversationId, upTo),
    onMutate: async ({ conversationId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<UnreadSummary>(queryKey);
      if (previous) {
        queryClient.setQueryData<UnreadSummary>(queryKey, withoutConversation(previous, conversationId));
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData<UnreadSummary>(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

export function usePatientConversations(auth: ApiAuth, { patientId, enabled }: UnreadOptions) {
  const active = enabled && !!patientId && !!auth.jwt;
  return useQuery<PatientConversationView[]>({
    queryKey: patientConversationsKey(patientId),
    queryFn: () => patientMessagingApi.listConversations(auth),
    enabled: active,
    staleTime: 15_000,
    refetchInterval: active ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
}

type ThreadOptions = {
  patientId: string | null | undefined;
  conversationId: string | null | undefined;
  enabled: boolean;
  limit?: number;
};

// First page only, no poll: the caller refetches on socket events and pages with `before`.
export function usePatientThread(auth: ApiAuth, { patientId, conversationId, enabled, limit }: ThreadOptions) {
  const active = enabled && !!patientId && !!auth.jwt && !!conversationId;
  return useQuery<ThreadPage>({
    queryKey: patientThreadKey(patientId, conversationId),
    queryFn: () => patientMessagingApi.listMessages(auth, conversationId as string, { limit }),
    enabled: active,
    staleTime: 15_000,
  });
}

export function useInvalidatePatientConversations(patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: patientConversationsKey(patientId) }),
      queryClient.invalidateQueries({ queryKey: patientUnreadKey(patientId) }),
    ]);
}

export function useStartDirectConversation(auth: ApiAuth, patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<DirectResult, unknown, MemberRef>({
    mutationFn: (other) => patientMessagingApi.startDirect(auth, other),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: patientConversationsKey(patientId) }),
  });
}

export function useLeaveConversation(auth: ApiAuth, patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<LeaveResult, unknown, string>({
    mutationFn: (conversationId) => patientMessagingApi.leaveConversation(auth, conversationId),
    onSuccess: (_result, conversationId) => {
      queryClient.removeQueries({ queryKey: patientThreadKey(patientId, conversationId) });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: patientConversationsKey(patientId) }),
        queryClient.invalidateQueries({ queryKey: patientUnreadKey(patientId) }),
      ]);
    },
  });
}

export function useDeleteMessage(auth: ApiAuth, patientId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<DeleteMessageResult, unknown, { conversationId: string; messageId: string }>({
    mutationFn: ({ conversationId, messageId }) =>
      patientMessagingApi.deleteMessage(auth, conversationId, messageId),
    onSuccess: (_result, { conversationId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: patientThreadKey(patientId, conversationId) }),
        queryClient.invalidateQueries({ queryKey: patientConversationsKey(patientId) }),
      ]),
  });
}
